import { useMemo, useEffect, useState } from "react";
import { CoachAppSetting, CoachSettings, SocialNetwork, SocialNetworksId } from "../../../interfaces";
import { PlayerCardsContextData } from "./PlayerCardsContext";
import { useSocialNetworks, useSocialProfiles } from "../../../utils";
import { SettingsServices } from "../../../services";
import { UserContextData } from "./UserContext";
import { SocialNetworkProfile } from "../../../services/bedrock";

type SocialNetworkSettings = {
  [K in SocialNetworksId]?: {
    isPermitted?: boolean;
    socialNetwork?: SocialNetwork;
  };
};

export interface SocialNetworkSettingsContextData {
  socialNetworkSettings: SocialNetworkSettings;
}

export const InitialSocialNetworkSettingsContextData: SocialNetworkSettingsContextData = {
  socialNetworkSettings: {},
};

const toSocialNetwork = (profile: SocialNetworkProfile): SocialNetwork => ({
  networkId: profile.socialNetworkId,
  username: profile.socialIdentity?.username || "",
  publicData: profile.socialIdentity?.publicUrl ? JSON.stringify({ link: profile.socialIdentity.publicUrl }) : null,
});

export const SocialNetworkSettingsContext = (
  PlayerCards: PlayerCardsContextData,
  User: UserContextData
): SocialNetworkSettingsContextData => {
  const [coachAppSettings, setCoachAppSettings] = useState<CoachAppSetting>();
  const { displayTikTok, displayGoogle, displayZillow } = useSocialNetworks();

  const userId = PlayerCards.selectedPlayerCard?.player?.userId ?? (User.isPlayer ? User.userId : undefined);
  const { socialProfiles } = useSocialProfiles(userId);

  useEffect(() => {
    if (!User.coachId) return;

    SettingsServices.getAppSettingsByCoachId(User.coachId.toString()).then(setCoachAppSettings);
  }, []);

  const socialNetworkSettings = useMemo(() => {
    if (!coachAppSettings) return {};

    const settingsHash = Object.fromEntries(
      coachAppSettings.settings.map(setting => [setting.appSettingId, (setting.status || "ACTIVE") === "ACTIVE"])
    );
    const networkIds: ("FB" | "IG" | "LI" | "TT" | "TW" | "GL" | "ZL")[] = ["FB", "IG", "LI", "TT", "TW", "GL", "ZL"];
    const nextSettings: SocialNetworkSettings = {};
    networkIds.forEach(networkId => {
      let isPermitted = settingsHash[CoachSettings[`POST_IN_${networkId}`]];
      if (networkId === "TT") {
        isPermitted = isPermitted && displayTikTok;
      } else if (networkId === "GL") {
        isPermitted = isPermitted && displayGoogle;
      } else if (networkId === "ZL") {
        isPermitted = isPermitted && displayZillow;
      }
      const socialProfile = socialProfiles.find(profile => profile.isActive && profile.socialNetworkId === networkId);
      nextSettings[networkId] = {
        isPermitted,
        socialNetwork: socialProfile ? toSocialNetwork(socialProfile) : undefined,
      };
    });
    return nextSettings;
  }, [coachAppSettings, socialProfiles, displayTikTok, displayGoogle, displayZillow]);

  return {
    socialNetworkSettings,
  };
};
